import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
} from '@angular/core';
import { DIALOG_CONFIG_FOR_FILTERS } from '@bindoc-projects/util-lib/ui';
import { MenuItem } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { SplitButton } from 'primeng/splitbutton';
import { take } from 'rxjs';

import { BdFolderLocationDialogComponent } from '../folder-location-dialog/folder-location-dialog.component';
import { BD_DIALOG_SERVICE_PROVIDER } from '@bindoc-projects/ui-lib/dialog/ui';

type SplitButtonSeverity =
  | 'success'
  | 'info'
  | 'warn'
  | 'danger'
  | 'help'
  | 'primary'
  | 'secondary'
  | 'contrast'
  | null
  | undefined;

/**
 * Save control for create pages: a PrimeNG split button whose primary action
 * is "Speichern" (emits {@link save}) and whose dropdown offers
 * "Speichern unter …", which opens the folder-location dialog and emits
 * {@link saveTo} with the chosen folder id. The caller performs the actual
 * create and — for `saveTo` — the subsequent move.
 */
@Component({
  selector: 'bd-save-split-button',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <p-splitbutton
      [label]="label()"
      [model]="menuItems"
      [disabled]="disabled()"
      [severity]="severity()"
      [outlined]="outlined()"
      [pt]="{ pcButton: { root: { 'data-test': dataTest() } } }"
      (onClick)="save.emit()"
    ></p-splitbutton>
  `,
  imports: [SplitButton],
  providers: [BD_DIALOG_SERVICE_PROVIDER],
})
export class BdSaveSplitButtonComponent {
  public readonly label = input('Speichern');
  public readonly disabled = input(false);
  public readonly outlined = input(false);
  public readonly severity = input<SplitButtonSeverity>(undefined);
  public readonly dataTest = input('save-split-button');

  public readonly save = output<void>();
  public readonly saveTo = output<string>();

  private readonly _dialogService = inject(DialogService);

  public readonly menuItems: MenuItem[] = [
    {
      label: 'Speichern unter …',
      icon: 'pi pi-folder',
      command: () => this._openFolderDialog(),
    },
  ];

  private _openFolderDialog(): void {
    this._dialogService
      .open(BdFolderLocationDialogComponent, {
        ...DIALOG_CONFIG_FOR_FILTERS,
        header: 'Speichern unter',
      })
      .onClose.pipe(take(1))
      .subscribe((folderId: string | null) => {
        if (folderId) {
          this.saveTo.emit(folderId);
        }
      });
  }
}
